import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface SubQuery {
  id: number;
  status: 'pending' | 'active' | 'completed' | 'error';
  details?: string;
  progress?: number;
  startTime?: number;
  endTime?: number;
}

export interface LegalQueryProgressStage {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  startTime?: number;
  endTime?: number;
  progress?: number; // 0-100 percentage
  details?: string;
  subQueries?: SubQuery[]; // For sub_query_processing stage
}

export interface LegalQueryProgress {
  queryId: string;
  chatId: string; // ✅ Add chat context
  caseId: string; // ✅ Add case context  
  isActive: boolean;
  currentStageIndex: number;
  stages: LegalQueryProgressStage[];
  startTime?: number;
  endTime?: number;
  overallProgress: number; // 0-100 percentage
  error?: string;
  lastMqttUpdate?: number; // ✅ Track when we last received MQTT updates
}

export interface LegalQueryProgressState {
  activeProgresses: Record<string, LegalQueryProgress>; // queryId -> progress
  lastCompletedQueryId?: string;
}

// Stage weights based on actual processing time (must sum to 100)
// Rebalanced for smoother progress bar progression
const STAGE_WEIGHTS = {
  'initialization': 5,        // Setup and preparation
  'decomposition': 15,        // Query analysis and breakdown  
  'sub_query_processing': 60, // Core processing (reduced from 75% to prevent large jumps)
  'synthesis': 15,            // Response compilation and integration
  'finalization': 5           // Final formatting and completion
};

// Helper function to calculate overall progress with realistic stage weights
const calculateOverallProgress = (stages: LegalQueryProgressStage[]): number => {
  let totalProgress = 0;
  const progressBreakdown: { [key: string]: { weight: number, contribution: number, status: string, progress?: number } } = {};
  
  for (const stage of stages) {
    const stageWeight = STAGE_WEIGHTS[stage.id as keyof typeof STAGE_WEIGHTS] || 0;
    let contribution = 0;
    
    if (stage.status === 'completed') {
      contribution = stageWeight;
    } else if (stage.status === 'active' && stage.progress !== undefined) {
      contribution = (stage.progress / 100) * stageWeight;
    }
    
    totalProgress += contribution;
    progressBreakdown[stage.id] = {
      weight: stageWeight,
      contribution,
      status: stage.status,
      progress: stage.progress
    };
  }
  
  console.log('[REDUX] Progress calculation breakdown:', progressBreakdown);
  console.log('[REDUX] Total calculated progress:', Math.round(totalProgress));
  console.log('[REDUX] Stage weights used:', STAGE_WEIGHTS);
  
  // ✅ Debug each stage contribution
  for (const stage of stages) {
    const stageWeight = STAGE_WEIGHTS[stage.id as keyof typeof STAGE_WEIGHTS] || 0;
    const stageContribution = stage.status === 'completed' ? stageWeight : 
                            (stage.status === 'active' && stage.progress !== undefined) ? 
                            (stage.progress / 100) * stageWeight : 0;
    console.log(`[REDUX] Stage ${stage.id}: status=${stage.status}, progress=${stage.progress}, weight=${stageWeight}, contribution=${stageContribution.toFixed(1)}`);
  }
  
  return Math.round(totalProgress);
};

// Default stages for legal query processing
const createDefaultStages = (): LegalQueryProgressStage[] => [
  {
    id: 'initialization',
    name: 'Initializing Query',
    description: 'Setting up legal analysis framework and tools',
    status: 'pending',
  },
  {
    id: 'decomposition',
    name: 'Query Decomposition',
    description: 'Breaking down complex legal query into focused sub-queries',
    status: 'pending',
  },
  {
    id: 'sub_query_processing',
    name: 'Processing Sub-Queries',
    description: 'Analyzing individual legal components with specialized tools',
    status: 'pending',
    subQueries: [], // Will be populated when sub-queries are discovered
  },
  {
    id: 'synthesis',
    name: 'Response Synthesis',
    description: 'Combining analyses into comprehensive legal response',
    status: 'pending',
  },
  {
    id: 'finalization',
    name: 'Finalizing Response',
    description: 'Applying legal formatting and citation standards',
    status: 'pending',
  },
];

const initialState: LegalQueryProgressState = {
  activeProgresses: {},
};

const legalQueryProgressSlice = createSlice({
  name: 'legalQueryProgress',
  initialState,
  reducers: {
    startProgress: (state, action: PayloadAction<{ queryId: string; chatId: string; caseId: string; query: string }>) => {
      const { queryId, chatId, caseId, query } = action.payload;
      console.log(`🎯 [PROGRESS_DEBUG] startProgress REDUCER called with queryId: ${queryId}, chatId: ${chatId}, caseId: ${caseId}`);
      console.log(`🎯 [PROGRESS_DEBUG] Current activeProgresses keys BEFORE: [${Object.keys(state.activeProgresses).join(', ')}]`);
      
      const stages = createDefaultStages();
      
      state.activeProgresses[queryId] = {
        queryId,
        chatId, // ✅ Store chat context
        caseId, // ✅ Store case context
        isActive: true,
        currentStageIndex: 0,
        stages,
        startTime: Date.now(),
        overallProgress: 0,
        lastMqttUpdate: Date.now(), // ✅ Track MQTT activity
      };
      
      console.log(`✅ [PROGRESS_DEBUG] startProgress REDUCER completed for queryId: ${queryId}`);
      console.log(`✅ [PROGRESS_DEBUG] Current activeProgresses keys AFTER: [${Object.keys(state.activeProgresses).join(', ')}]`);
      console.log(`✅ [PROGRESS_DEBUG] Created progress object:`, state.activeProgresses[queryId]);
    },

    // ✅ Note: This reducer is deprecated in favor of MQTT-driven progress
    // It's kept for backward compatibility but should not be used for new progress restoration
    restoreLegalProgress: (state, action: PayloadAction<{ 
      queryId: string; 
      chatId?: string; 
      caseId?: string; 
      startTime?: string; 
      subQueryCount?: number 
    }>) => {
      const { queryId, chatId = 'unknown', caseId = 'unknown', startTime, subQueryCount = 2 } = action.payload;
      console.log(`🔄 [RESTORE_REDUCER] RESTORING progress for queryId: ${queryId}, chatId: ${chatId}, caseId: ${caseId}`);
      console.log(`🔄 [RESTORE_REDUCER] Current state has queryIds: [${Object.keys(state.activeProgresses).join(', ')}]`);
      
      const stages = createDefaultStages();
      
      // Mark initialization as completed and decomposition as active
      stages[0].status = 'completed';
      stages[0].progress = 100;
      stages[0].endTime = Date.now();
      
      stages[1].status = 'active';
      stages[1].startTime = Date.now();
      stages[1].details = 'Legal analysis in progress... (restored from page refresh)';
      stages[1].progress = 50;

      // Initialize sub-queries in the sub_query_processing stage
      const subQueryStage = stages.find(stage => stage.id === 'sub_query_processing');
      if (subQueryStage) {
        subQueryStage.subQueries = Array.from({ length: subQueryCount }, (_, index) => ({
          id: index,
          status: 'pending',
          details: 'Sub-query analysis pending...',
          progress: 0
        }));
      }
      
      const restoredProgress: LegalQueryProgress = {
        queryId,
        chatId, // ✅ Add required chatId
        caseId, // ✅ Add required caseId
        isActive: true,
        currentStageIndex: 1, // Currently in decomposition stage
        stages,
        startTime: startTime ? new Date(startTime).getTime() : Date.now(),
        overallProgress: calculateOverallProgress(stages), // Will be around 20-25% based on weights
        lastMqttUpdate: Date.now(), // ✅ Add MQTT timestamp
      };

      state.activeProgresses[queryId] = restoredProgress;
      console.log(`✅ [RESTORE_REDUCER] RESTORED progress object for queryId: ${queryId}`);
      console.log(`✅ [RESTORE_REDUCER] State now has queryIds: [${Object.keys(state.activeProgresses).join(', ')}]`);
      console.log(`✅ [RESTORE_REDUCER] Restored with overall progress: ${restoredProgress.overallProgress}%`);
    },

    updateStageProgress: (state, action: PayloadAction<{
      queryId: string;
      stageId: string;
      status?: 'pending' | 'active' | 'completed' | 'error';
      progress?: number;
      details?: string;
    }>) => {
      const { queryId, stageId, status, progress, details } = action.payload;
      console.log(`🔄 [PROGRESS_DEBUG] updateStageProgress called for queryId: ${queryId}, stage: ${stageId}`);
      console.log(`🔄 [PROGRESS_DEBUG] Available queryIds in state: [${Object.keys(state.activeProgresses).join(', ')}]`);
      
      const queryProgress = state.activeProgresses[queryId];
      
      // ✅ Update MQTT timestamp when we receive updates
      if (queryProgress) {
        queryProgress.lastMqttUpdate = Date.now();
      }
      
      if (!queryProgress) {
        console.error(`🚨 [PROGRESS_DEBUG] NO PROGRESS FOUND! queryId "${queryId}" not found in activeProgresses`);
        console.error(`🚨 [PROGRESS_DEBUG] This means startProgress was never called or there's a queryId mismatch`);
        console.error(`🚨 [PROGRESS_DEBUG] Available queryIds: [${Object.keys(state.activeProgresses).join(', ')}]`);
        console.error(`🚨 [PROGRESS_DEBUG] Looking for queryId: "${queryId}"`);
        console.error(`🚨 [PROGRESS_DEBUG] MQTT update will be IGNORED, causing stuck progress!`);
        return;
      }
      
      console.log(`✅ [PROGRESS_DEBUG] Found progress object for queryId: ${queryId}`);
      
      const stageIndex = queryProgress.stages.findIndex(stage => stage.id === stageId);
      if (stageIndex === -1) {
        console.error(`🚨 [PROGRESS_DEBUG] Stage "${stageId}" not found in stages`);
        return;
      }
      
      const stage = queryProgress.stages[stageIndex];
      
      // Don't go backwards: if current progress is ahead of this stage, only allow forward progress
      const currentStageIndex = queryProgress.currentStageIndex;
      const isBackwardsUpdate = stageIndex < currentStageIndex && 
                               status !== 'completed' && 
                               status !== 'error';
      
      if (isBackwardsUpdate) {
        console.log(`🛑 [PROGRESS_DEBUG] Ignoring backwards update: trying to set stage ${stageIndex} (${stageId}) when current stage is ${currentStageIndex}`);
        return;
      }
      
      if (status) {
        const oldStatus = stage.status;
        
        // Don't downgrade a completed stage to active/pending unless it's an error
        if (stage.status === 'completed' && status !== 'error') {
          console.log(`🛑 [PROGRESS_DEBUG] Ignoring downgrade of completed stage ${stageId} from ${oldStatus} to ${status}`);
          // Still update details if provided
          if (details) {
            stage.details = details;
          }
          return;
        }
        
        stage.status = status;
        
        if (status === 'active') {
          // Only update current stage index if we're moving forward
          if (stageIndex >= currentStageIndex) {
            queryProgress.currentStageIndex = stageIndex;
          }
          if (!stage.startTime) {
            stage.startTime = Date.now();
          }
          // For sub-query processing stage, don't reset progress if it exists
          if (stage.id === 'sub_query_processing' && stage.progress === undefined) {
            stage.progress = 0;
          }
        }
        
        if (status === 'completed' && !stage.endTime) {
          stage.endTime = Date.now();
          stage.progress = 100;
        }
        
        if (status === 'error') {
          queryProgress.error = details || 'Stage processing failed';
          stage.progress = 0;
        }
      }
      
      if (progress !== undefined) {
        // Don't decrease progress on a stage
        const newProgress = Math.max(0, Math.min(100, progress));
        if (stage.progress === undefined || newProgress >= stage.progress) {
          stage.progress = newProgress;
        } else {
          console.log(`🛑 [PROGRESS_DEBUG] Ignoring progress decrease for stage ${stageId}: ${stage.progress}% -> ${newProgress}%`);
        }
      }
      
      if (details) {
        stage.details = details;
      }
      
      // Only update overall progress if it's moving forward or it's an error state
      const oldOverallProgress = queryProgress.overallProgress;
      const newOverallProgress = calculateOverallProgress(queryProgress.stages);
      
      if (newOverallProgress >= oldOverallProgress || status === 'error') {
        queryProgress.overallProgress = newOverallProgress;
      }
      
      console.log(`[REDUX] Progress update for ${queryId}: stage=${stageId}, status=${status}, overall=${queryProgress.overallProgress}%`);
    },

    advanceToNextStage: (state, action: PayloadAction<{ queryId: string; details?: string }>) => {
      const { queryId, details } = action.payload;
      const queryProgress = state.activeProgresses[queryId];
      
      if (!queryProgress) return;
      
      const currentStage = queryProgress.stages[queryProgress.currentStageIndex];
      if (!currentStage) return;
      
      // Complete current stage
      if (currentStage.status === 'active') {
        currentStage.status = 'completed';
        currentStage.endTime = Date.now();
        currentStage.progress = 100;
        if (details) currentStage.details = details;
      }
      
      // Move to next stage
      if (queryProgress.currentStageIndex < queryProgress.stages.length - 1) {
        queryProgress.currentStageIndex += 1;
        const nextStage = queryProgress.stages[queryProgress.currentStageIndex];
        nextStage.status = 'active';
        nextStage.startTime = Date.now();
      }
      
      // Use the new weighted progress calculation
      queryProgress.overallProgress = calculateOverallProgress(queryProgress.stages);
    },

    completeProgress: (state, action: PayloadAction<{ queryId: string; success: boolean; details?: string }>) => {
      const { queryId, success, details } = action.payload;
      console.log('[REDUX] Completing progress for query:', queryId, 'success:', success);
      const queryProgress = state.activeProgresses[queryId];
      
      if (!queryProgress) return;
      
      queryProgress.isActive = false;
      queryProgress.endTime = Date.now();
      
      if (success) {
        // Mark all stages as completed
        queryProgress.stages.forEach(stage => {
          if (stage.status !== 'completed') {
            stage.status = 'completed';
            stage.progress = 100;
            if (!stage.endTime) stage.endTime = Date.now();
          }
        });
        queryProgress.overallProgress = 100;
        state.lastCompletedQueryId = queryId;
      } else {
        // Mark current stage as error
        const currentStage = queryProgress.stages[queryProgress.currentStageIndex];
        if (currentStage) {
          currentStage.status = 'error';
          currentStage.details = details || 'Processing failed';
        }
        queryProgress.error = details || 'Query processing failed';
      }
    },

    clearProgress: (state, action: PayloadAction<{ queryId: string }>) => {
      const { queryId } = action.payload;
      delete state.activeProgresses[queryId];
    },

    clearAllProgress: (state) => {
      state.activeProgresses = {};
      state.lastCompletedQueryId = undefined;
    },

    // ✅ New: Clean up stale progresses based on MQTT activity
    cleanupStaleProgresses: (state) => {
      const now = Date.now();
      const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes without MQTT updates
      
      const staleQueryIds: string[] = [];
      
      Object.entries(state.activeProgresses).forEach(([queryId, progress]) => {
        const lastUpdate = progress.lastMqttUpdate || progress.startTime || 0;
        const timeSinceUpdate = now - lastUpdate;
        
        if (timeSinceUpdate > STALE_THRESHOLD) {
          staleQueryIds.push(queryId);
        }
      });
      
      // Remove stale progresses
      staleQueryIds.forEach(queryId => {
        console.log(`🧹 [CLEANUP] Removing stale progress for queryId: ${queryId}`);
        delete state.activeProgresses[queryId];
      });
      
      if (staleQueryIds.length > 0) {
        console.log(`🧹 [CLEANUP] Removed ${staleQueryIds.length} stale progresses`);
      }
    },

    updateSubQueryProgress: (state, action: PayloadAction<{
      queryId: string;
      subQueryIndex: number;
      subQueryTotal: number;
      subQueryDetails?: string;
      progress?: number;
      status?: 'pending' | 'active' | 'completed' | 'error';
    }>) => {
      const { queryId, subQueryIndex, subQueryTotal, subQueryDetails, progress, status } = action.payload;
      const queryProgress = state.activeProgresses[queryId];
      
      if (!queryProgress) return;
      
      // ✅ Update MQTT timestamp when we receive sub-query updates
      queryProgress.lastMqttUpdate = Date.now();
      
      // Find the sub-query processing stage
      const subQueryStage = queryProgress.stages.find(stage => stage.id === 'sub_query_processing');
      if (!subQueryStage) return;
      
      // Initialize sub-queries array if needed
      if (!subQueryStage.subQueries) {
        subQueryStage.subQueries = [];
      }
      
      // Ensure we have enough sub-query slots
      while (subQueryStage.subQueries.length < subQueryTotal) {
        subQueryStage.subQueries.push({
          id: subQueryStage.subQueries.length,
          status: 'pending'
        });
      }
      
      // Initialize stage progress to 0% if this is the first time we're setting up sub-queries
      if (subQueryStage.progress === undefined) {
        subQueryStage.progress = 0;
      }
      
      // Track the previous status to detect actual events/changes
      const subQuery = subQueryStage.subQueries[subQueryIndex];
      const previousStatus = subQuery?.status;
      
      console.log(`[REDUX] Sub-query ${subQueryIndex} update: ${previousStatus} -> ${status} (previousStatus=${previousStatus}, defined=${previousStatus !== undefined})`);
      
      // Update the specific sub-query
      if (subQuery) {
        if (status) subQuery.status = status;
        if (progress !== undefined) subQuery.progress = progress;
        if (subQueryDetails) subQuery.details = subQueryDetails;
        if (status === 'active' && !subQuery.startTime) subQuery.startTime = Date.now();
        if (status === 'completed' && !subQuery.endTime) subQuery.endTime = Date.now();
      }
      
      // Calculate progress incrementally based on actual events
      // Each sub-query has 2 events: 'pending->active' and 'active->completed'
      const EVENTS_PER_SUBQUERY = 2;
      const totalPossibleEvents = subQueryTotal * EVENTS_PER_SUBQUERY;
      const incrementPerEvent = 100 / totalPossibleEvents;
      
      // Only increment progress if there was an actual status change (event)
      const shouldIncrement = status && status !== previousStatus && previousStatus !== undefined;
      console.log(`[REDUX] Increment check: shouldIncrement=${shouldIncrement}, status=${status}, previousStatus=${previousStatus}, totalEvents=${totalPossibleEvents}, increment=${incrementPerEvent.toFixed(1)}%`);
      
      if (shouldIncrement) {
        let incrementToAdd = 0;
        
        if (status === 'active' && previousStatus === 'pending') {
          // Event: sub-query started
          incrementToAdd = incrementPerEvent;
          console.log(`[REDUX] Event: sub-query ${subQueryIndex} started, adding ${incrementPerEvent.toFixed(1)}%`);
        } else if (status === 'completed' && previousStatus === 'active') {
          // Event: sub-query completed
          incrementToAdd = incrementPerEvent;
          console.log(`[REDUX] Event: sub-query ${subQueryIndex} completed, adding ${incrementPerEvent.toFixed(1)}%`);
        } else if (status === 'error' && (previousStatus === 'pending' || previousStatus === 'active')) {
          // Event: sub-query errored (count as completion for progress)
          const eventsToAdd = previousStatus === 'pending' ? 2 : 1; // If pending->error, count as 2 events
          incrementToAdd = incrementPerEvent * eventsToAdd;
          console.log(`[REDUX] Event: sub-query ${subQueryIndex} errored from ${previousStatus}, adding ${(incrementPerEvent * eventsToAdd).toFixed(1)}%`);
        } else {
          console.log(`[REDUX] Unknown transition: ${previousStatus} -> ${status}, no increment added`);
        }
        
        if (incrementToAdd > 0) {
          const oldProgress = subQueryStage.progress;
          subQueryStage.progress += incrementToAdd;
          
          // Round to avoid floating point precision issues
          subQueryStage.progress = Math.round(subQueryStage.progress * 100) / 100;
          
          // Cap at 100% (should not exceed, but just in case)
          if (subQueryStage.progress > 100) {
            subQueryStage.progress = 100;
          }
          
          console.log(`[REDUX] Progress updated: ${oldProgress?.toFixed(1)}% -> ${subQueryStage.progress?.toFixed(1)}%`);
        }
      } else {
        console.log(`[REDUX] No increment: status unchanged or first initialization`);
      }

      // Update stage details
      const completedCount = subQueryStage.subQueries.filter(sq => sq.status === 'completed').length;
      const activeCount = subQueryStage.subQueries.filter(sq => sq.status === 'active').length;
      const errorCount = subQueryStage.subQueries.filter(sq => sq.status === 'error').length;
      const pendingCount = subQueryStage.subQueries.filter(sq => sq.status === 'pending').length;
      
      subQueryStage.details = `${completedCount} completed, ${activeCount} active (${subQueryTotal} total)`;

      // Use the new weighted progress calculation
      const oldOverallProgress = queryProgress.overallProgress;
      queryProgress.overallProgress = calculateOverallProgress(queryProgress.stages);

      console.log(`[REDUX] Sub-query stage progress now: ${subQueryStage.progress?.toFixed(1)}% | Event: ${previousStatus}->${status} | Sub-query ${subQueryIndex}/${subQueryTotal} | Overall: ${oldOverallProgress}% -> ${queryProgress.overallProgress}%`);
      
      // ✅ Explicit debug: Check if overall progress is actually changing
      if (oldOverallProgress !== queryProgress.overallProgress) {
        console.log(`🎯 [PROGRESS_UPDATE] Overall progress CHANGED: ${oldOverallProgress}% -> ${queryProgress.overallProgress}%`);
      } else {
        console.log(`⚠️ [PROGRESS_UPDATE] Overall progress UNCHANGED: ${queryProgress.overallProgress}%`);
      }
    },
  },
});

export const {
  startProgress,
  restoreLegalProgress,
  updateStageProgress,
  advanceToNextStage,
  completeProgress,
  clearProgress,
  clearAllProgress,
  cleanupStaleProgresses, // ✅ Export new cleanup action
  updateSubQueryProgress,
} = legalQueryProgressSlice.actions;

// ✅ New: Selectors for chat-specific progress checking
export const selectActiveProgressForChat = (state: { legalQueryProgress: LegalQueryProgressState }, chatId: string) => {
  return Object.values(state.legalQueryProgress.activeProgresses).find(
    progress => progress.chatId === chatId && progress.isActive
  );
};

export const selectHasActiveProgressForChat = (state: { legalQueryProgress: LegalQueryProgressState }, chatId: string) => {
  const activeProgress = selectActiveProgressForChat(state, chatId);
  if (!activeProgress) return false;
  
  // ✅ Additional check: ensure MQTT activity is recent
  const now = Date.now();
  const lastUpdate = activeProgress.lastMqttUpdate || activeProgress.startTime || 0;
  const timeSinceUpdate = now - lastUpdate;
  const ACTIVITY_THRESHOLD = 2 * 60 * 1000; // 2 minutes
  
  return timeSinceUpdate <= ACTIVITY_THRESHOLD;
};

export default legalQueryProgressSlice.reducer; 